/**
 * ElementPreview - Preview de elemento antes de confirmar creación
 *
 * @version 2.0
 * @updated 2026-02-20
 * @module core/map-tools
 *
 * @description
 * Popover flotante que aparece en el lugar exacto del click, preguntando
 * "¿Crear árbol aquí?" sin bloquear la UI. El usuario puede cancelar haciendo
 * click fuera o seguir buscando el mejor lugar.
 *
 * @remarks
 * **Mejoras v2.0**:
 * - Popover en lugar de modal (no bloquea UI)
 * - "Punta" visual apuntando al lugar exacto
 * - Click fuera para cancelar
 * - Más compacto y dentro del flujo de trabajo
 *
 * **Flujo**:
 * 1. Usuario hace clic en el mapa
 * 2. Aparece popover flotante sobre el punto de click
 * 3. "Punta" apunta exactamente donde se creará el elemento
 * 4. Usuario confirma (Enter) → se crea el elemento
 * 5. Usuario cancela (click fuera o Q) → desaparece
 */

import { useEffect, useRef } from 'react';
import { Check, X, TreeDeciduous, Trash2, Route } from 'lucide-react';
import { mapEngine } from '../map-engine/MapEngineService';
import './element-preview.css';

/**
 * Tipo de elemento en preview
 */
export type PreviewElementType = 'tree' | 'park' | 'fountain' | 'sunshade' | 'water-course' | 'water-mirror' | 'container' | 'route';

/**
 * Props del componente
 */
export interface ElementPreviewProps {
  /**
   * Tipo de elemento
   */
  type: PreviewElementType;

  /**
   * Coordenadas [lng, lat] donde se creará el elemento
   */
  coordinates: [number, number];

  /**
   * Callback al confirmar creación
   */
  onConfirm: () => void;

  /**
   * Callback al cancelar
   */
  onCancel: () => void;
}

/**
 * Etiquetas por tipo de elemento (sin emojis, usando texto)
 */
const ELEMENT_LABELS: Record<PreviewElementType, string> = {
  tree: 'árbol',
  park: 'parque',
  fountain: 'fuente',
  sunshade: 'parasol',
  'water-course': 'curso de agua',
  'water-mirror': 'espejo de agua',
  container: 'contenedor',
  route: 'punto de inicio de ruta',
};

/**
 * Iconos por tipo de elemento
 */
const ELEMENT_ICONS: Record<PreviewElementType, React.ReactNode> = {
  tree: <TreeDeciduous size={16} />,
  park: <TreeDeciduous size={16} />,
  fountain: <TreeDeciduous size={16} />,
  sunshade: <TreeDeciduous size={16} />,
  'water-course': <TreeDeciduous size={16} />,
  'water-mirror': <TreeDeciduous size={16} />,
  container: <Trash2 size={16} />,
  route: <Route size={16} />,
};

/**
 * Componente ElementPreview
 */
export function ElementPreview({
  type,
  coordinates,
  onConfirm,
  onCancel,
}: ElementPreviewProps): JSX.Element {
  const popoverRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const label = ELEMENT_LABELS[type];

  /**
   * Calcula posición en píxeles a partir de coordenadas geográficas
   * y actualiza cuando el mapa se mueve
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !popoverRef.current || !lineRef.current) return;

    const updatePosition = (): void => {
      if (!map || !popoverRef.current || !lineRef.current) return;

      // Convertir coordenadas geográficas a píxeles del mapa
      const point = map.project([coordinates[0], coordinates[1]]);

      const popoverWidth = popoverRef.current.offsetWidth;
      const popoverHeight = popoverRef.current.offsetHeight;

      // El mapa tiene un offset por el TopBar (120px * ui-scale)
      // Obtener el valor de --ui-scale
      const uiScale = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale')) || 1.0;
      const topBarHeight = 120 * uiScale;

      // Ajustar las coordenadas agregando el offset del topbar
      const adjustedY = point.y + topBarHeight;

      // Distancia total fija desde el bottom del popover hasta el punto de clic
      const totalGap = 80;

      // Posicionar popover: su bottom estará a totalGap px arriba del punto de clic
      popoverRef.current.style.left = `${point.x - popoverWidth / 2}px`;
      popoverRef.current.style.top = `${adjustedY - totalGap - popoverHeight}px`;

      // La línea llena todo el gap desde el bottom del popover hasta el punto de clic
      lineRef.current.style.left = `${point.x}px`;
      lineRef.current.style.top = `${adjustedY - totalGap}px`;
      lineRef.current.style.height = `${totalGap}px`;
    };

    // Posicionar inicialmente
    updatePosition();

    // Actualizar posición cuando el mapa se mueve o hace zoom
    map.on('move', updatePosition);
    map.on('zoom', updatePosition);

    return () => {
      map.off('move', updatePosition);
      map.off('zoom', updatePosition);
    };
  }, [coordinates]);

  /**
   * Maneja teclas Q (cancelar) y Enter (confirmar)
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'q' || e.key === 'Q') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, onConfirm]);

  /**
   * Click fuera del popover = cancelar
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };

    // Pequeño delay para no capturar el click que abrió el popover
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onCancel]);

  return (
    <>
      {/* Línea de conexión al punto de clic */}
      <div ref={lineRef} className="element-preview-line" />

      <div
        ref={popoverRef}
        className="element-preview-popover"
      >
        {/* Punta que apunta hacia abajo */}
        <div className="element-preview-arrow" />

      {/* Contenido compacto */}
      <div className="element-preview-content">
        {ELEMENT_ICONS[type]}
        <span className="element-preview-question">¿Crear {label} aquí?</span>
      </div>

      {/* Acciones compactas */}
      <div className="element-preview-actions">
        <button
          className="element-preview-btn element-preview-btn-cancel"
          onClick={onCancel}
          title="Cancelar (Q)"
        >
          <X size={14} />
        </button>
        <button
          className="element-preview-btn element-preview-btn-confirm"
          onClick={onConfirm}
          title="Confirmar (Enter)"
        >
          <Check size={14} />
        </button>
      </div>
    </div>
    </>
  );
}
